<div class="flex min-h-screen bg-gray-50">
  <!-- Sidebar -->
  <aside
    [class.w-64]="!sidebarCollapsed()"
    [class.w-16]="sidebarCollapsed()"
    class="bg-white shadow-lg border-r border-gray-200 transition-all duration-300 ease-in-out flex flex-col">

    <!-- Header -->
    <div class="p-4 border-b border-gray-200">
      <div class="flex items-center justify-between">
        <div *ngIf="!sidebarCollapsed()" class="flex items-center">
          <div class="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
            <sl-icon name="briefcase" class="text-white text-sm"></sl-icon>
          </div>
          <div>
            <h1 class="text-lg font-bold text-gray-900">HRMS</h1>
            <p class="text-xs text-gray-500">Human Resources</p>
          </div>
        </div>

        <button
          (click)="toggleSidebar()"
          class="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200">
          <sl-icon
            [name]="sidebarCollapsed() ? 'chevron-right' : 'chevron-left'"
            class="text-gray-500 text-sm">
          </sl-icon>
        </button>
      </div>
    </div>

    <!-- Navigation -->
    <nav class="flex-1 p-4 overflow-y-auto">
      <ul class="space-y-2">
        <li *ngFor="let item of menuItems()">
          <!-- Main Menu Item -->
          <div>
            <button
              (click)="item.children ? toggleMenu(item.id) : navigateToRoute(item.route)"
              [class.bg-blue-50]="isActiveRoute(item.route)"
              [class.border-blue-200]="isActiveRoute(item.route)"
              [class.text-blue-700]="isActiveRoute(item.route)"
              [class.text-gray-700]="!isActiveRoute(item.route)"
              class="w-full flex items-center p-3 rounded-lg border border-transparent hover:bg-gray-50 transition-all duration-200 group">

              <div class="flex items-center flex-1">
                <sl-icon
                  [name]="item.icon"
                  [class.text-blue-600]="isActiveRoute(item.route)"
                  [class.text-gray-500]="!isActiveRoute(item.route)"
                  class="text-lg mr-3 group-hover:scale-110 transition-transform duration-200">
                </sl-icon>

                <span
                  *ngIf="!sidebarCollapsed()"
                  class="font-medium text-sm">
                  {{ item.label }}
                </span>
              </div>

              <!-- Badge -->
              <span
                *ngIf="item.badge && !sidebarCollapsed()"
                [class]="item.badge.color"
                class="px-2 py-1 text-xs font-bold text-white rounded-full ml-2">
                {{ item.badge.text }}
              </span>

              <!-- Expand/Collapse Icon -->
              <sl-icon
                *ngIf="item.children && !sidebarCollapsed()"
                [name]="isMenuExpanded(item.id) ? 'chevron-down' : 'chevron-right'"
                class="text-gray-400 text-sm ml-2 transition-transform duration-200">
              </sl-icon>
            </button>

            <!-- Submenu -->
            <div
              *ngIf="item.children && isMenuExpanded(item.id) && !sidebarCollapsed()"
              class="mt-2 ml-6 space-y-1 border-l-2 border-gray-100 pl-4">

              <button
                *ngFor="let child of item.children"
                (click)="navigateToRoute(child.route)"
                [class.bg-blue-50]="isActiveRoute(child.route)"
                [class.text-blue-600]="isActiveRoute(child.route)"
                [class.text-gray-600]="!isActiveRoute(child.route)"
                class="w-full flex items-center p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200 group">

                <sl-icon
                  [name]="child.icon"
                  [class.text-blue-500]="isActiveRoute(child.route)"
                  [class.text-gray-400]="!isActiveRoute(child.route)"
                  class="text-sm mr-3">
                </sl-icon>

                <span class="text-sm font-medium">{{ child.label }}</span>
              </button>
            </div>
          </div>
        </li>
      </ul>
    </nav>

    <!-- User Profile Section -->
    <div class="p-4 border-t border-gray-200">
      <div class="flex items-center">
        <div class="w-10 h-10 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center mr-3">
          <sl-icon name="person" class="text-white text-sm"></sl-icon>
        </div>

        <div *ngIf="!sidebarCollapsed()" class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-900 truncate">{{ authService.user()?.name || 'User' }}</p>
          <p class="text-xs text-gray-500 truncate">{{ authService.user()?.email || 'user@company.com' }}</p>
          <p *ngIf="authService.user()?.roles && authService.user()?.roles!.length > 0"
             class="text-xs text-blue-600 truncate">
            {{ authService.user()?.roles![0] }}
          </p>
        </div>

        <button
          *ngIf="!sidebarCollapsed()"
          (click)="logout()"
          class="p-2 rounded-lg hover:bg-red-100 transition-colors duration-200"
          title="Logout">
          <sl-icon name="box-arrow-right" class="text-red-500 text-sm"></sl-icon>
        </button>
      </div>
    </div>
  </aside>

  <!-- Main Content -->
  <main class="flex-1 flex flex-col min-w-0">
    <!-- Top Bar -->
    <header class="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <h2 class="text-xl font-semibold text-gray-900">
            {{ getCurrentPageTitle() }}
          </h2>
        </div>

        <div class="flex items-center space-x-4">
          <!-- Notifications -->
          <button class="relative p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200">
            <sl-icon name="bell" class="text-gray-500 text-lg"></sl-icon>
            <span class="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              3
            </span>
          </button>

          <!-- Search -->
          <button class="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200">
            <sl-icon name="search" class="text-gray-500 text-lg"></sl-icon>
          </button>

          <!-- Settings -->
          <button class="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200">
            <sl-icon name="person-fill-gear" class="text-gray-500 text-lg"></sl-icon>
          </button>
        </div>
      </div>
    </header>

    <!-- Page Content -->
    <div class="flex-1 overflow-auto">
      <router-outlet></router-outlet>
    </div>
  </main>
</div>
